import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  IoSearch,
  IoTrashOutline,
  IoFilter,
  IoClose,
  IoChevronDown,
  IoChevronUp,
  IoEyeOutline,
} from "react-icons/io5";
import { IoMdArchive } from "react-icons/io";
import { MdOutlineWifiTetheringErrorRounded } from "react-icons/md";
import Search from "../../Jobick/Jobs/ui/search";
import { toast } from "react-toastify";
import TaskService from "../../../../services/TaskService";
import CategoryService from "../../../../services/CategoryService";
import { colors } from "../../../config/colors";
import { HiOutlinePencilAlt } from "react-icons/hi";
import { getChargeOutRateTitle } from "../../../utils/jobTitleLabel";

import DeleteModal from "../../shared/DeleteModal";

const ListTask = () => {
  const navigate = useNavigate();
  const chargeOutRateTitle = getChargeOutRateTitle();
  const [pageVal, setPageVal] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCost, setTotalCost] = useState(0);
  const [loading, setLoading] = useState(1);
  const [taskList, setTaskList] = useState([]);
  // const [totalTime, setTotalTime] = useState("0h 0m");
  const [searchVal, setSearchVal] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const tasksPerPage = 10; // Fixed limit of 10 items per page
  const [filters, setFilters] = useState({
    category: "",
    minCost: "",
    maxCost: "",
    minTime: "",
    maxTime: "",
  });
  const dropdownRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [showArchivedTasks, setShowArchivedTasks] = useState(false);
  const SEARCH_DEBOUNCE_DELAY = 500;
  const [archivingTask, setArchivingTask] = useState(null);

  const handlePreviousPageClick = () => {
    if (pageVal === 1) return;
    setPageVal(pageVal - 1);
  };

  const handleNextPageClick = () => {
    if (pageVal >= totalPages) return;
    setPageVal(pageVal + 1);
  };

  const handleDeleteClick = (taskId) => {
    setTaskToDelete(taskId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await TaskService.deleteTask(taskToDelete);
      if (response.success) {
        toast.success("Task deleted successfully");
        fetchTaskList();
      } else {
        throw new Error("Failed to delete task");
      }
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    } finally {
      setShowDeleteModal(false);
      setTaskToDelete(null);
    }
  };

  const handleFilterApply = () => {
    setPageVal(1);
    fetchTaskList(1, tasksPerPage, searchVal, selectedCategory);
    setShowFilterModal(false);
  };

  const handleClearFilter = () => {
    setSelectedCategory("");
    setPageVal(1);
    fetchTaskList(1, tasksPerPage, searchVal, "");
  };

  const handleClearAllFilters = () => {
    setSearchVal("");
    setSelectedCategory("");
    setPageVal(1);
    fetchTaskList(1, tasksPerPage, "", "");
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategories(prev => {
      const isSelected = prev.includes(categoryId);
      if (isSelected) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const fetchCategories = async () => {
    try {
      const response = await CategoryService.getAllCategories(false);
      if (response.success) {
        setCategories(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    }
  };

  const fetchTaskList = async (
    page = pageVal,
    limit = tasksPerPage,
    search = searchVal,
    categoryFilters = selectedCategories
  ) => {
    setLoading(1);
    try {
      const response = await TaskService.getTasks(
        page,
        limit,
        search,
        categoryFilters.join(','), // Join category IDs with comma
        showArchivedTasks
      );

      if (response.success) {
        setTaskList(response.data || []);
        // const totalTime = taskList.reduce((acc, task) => acc + (task.estimatedTime || 0), 0) / 60;
        // const totalHours = Math.floor(totalTime);
        // const totalMinutes = Math.round((totalTime - totalHours) * 60);
        // setTotalTime(`${totalHours}h ${totalMinutes}m`);

        setTotalTasks(response.total || 0);
        setTotalPages(response.totalPages || 1);
        setLoading(0);
        setTotalCost(response.totalAmount || 0);
      } else {
        throw new Error(response.message || "Failed to fetch tasks");
      }
    } catch (error) {
      setLoading(2);
      console.error("Error fetching task list:", error);
      toast.error(error.message || "Failed to fetch tasks");
    }
  };

  useEffect(() => {
    fetchTaskList(pageVal, tasksPerPage, searchVal, selectedCategories);
  }, [pageVal, showArchivedTasks]);

  useEffect(() => {
    fetchTaskList(pageVal, tasksPerPage, searchVal, selectedCategories);
  }, [selectedCategories, showArchivedTasks]);

  useEffect(() => {
    if (pageVal === 1) {
      fetchTaskList(1, tasksPerPage, searchVal, selectedCategories);
    } else {
      setPageVal(1);
    }
  }, [searchVal, showArchivedTasks]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreateTask = () => {
    navigate("/create-task");
  };

  const handleEditClick = (taskId) => {
    navigate(`/edit-task/${taskId}`);
  };

  // Add pagination calculations
  const pgItem1 =
    pageVal === 1
      ? 1
      : pageVal >= totalPages && totalPages > 2
        ? totalPages - 2
        : pageVal - 1;
  const pgItem2 =
    pageVal === 1
      ? 2
      : pageVal >= totalPages && totalPages > 2
        ? totalPages - 1
        : pageVal;
  const pgItem3 =
    pageVal === 1
      ? 3
      : pageVal >= totalPages && totalPages > 2
        ? totalPages
        : pageVal + 1;

  // Add the debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchVal(value);
    }, SEARCH_DEBOUNCE_DELAY),
    []
  );

  // Add the input change handler
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value);
  };

  // Add the debounce utility function
  function debounce(func, wait) {
    let timeout;
    const debouncedFunc = function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
    debouncedFunc.cancel = function () {
      clearTimeout(timeout);
    };
    return debouncedFunc;
  }

  // Add cleanup effect for debounced function
  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const filteredCategories = categories?.filter(cat => cat.categoryType.toLowerCase().includes(categorySearchTerm.toLowerCase()));

  const handleArchiveTask = async (taskId, isArchived = true) => {
    try {
      setArchivingTask(taskId);
      const response = await TaskService.archiveTask(taskId, isArchived);
      if (response.success) {
        toast.success(isArchived ? "Task archived successfully" : "Task unarchived successfully");
        fetchTaskList();
      } else {
        throw new Error(`Failed to ${isArchived ? 'archive' : 'unarchive'} task`);
      }
    } catch (error) {
      console.error(`Error ${isArchived ? 'archiving' : 'unarchiving'} task:`, error);
      toast.error(`Failed to ${isArchived ? 'archive' : 'unarchive'} task`);
    } finally {
      setArchivingTask(null);
    }
  };

  return (
    <div className="h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-white to-accent-light/10 p-6">
      {/* Header Section with Stats */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <div>
            <h2 className="text-2xl font-bold text-primary-headings mb-2">
              {showArchivedTasks ? 'Archived Tasks' : 'Task Management'}
            </h2>
            <p className="text-sm text-primary-light mb-6">
              {showArchivedTasks ? 'View and manage archived tasks' : 'Track and manage your tasks efficiently'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowArchivedTasks(!showArchivedTasks)}
              className={`inline-flex items-center px-4 py-2 rounded-md border transition-colors duration-200 shadow-sm whitespace-nowrap ${showArchivedTasks
                ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
            >
              <IoMdArchive className="h-4 w-4 mr-2" />
              <span className="text-sm">{showArchivedTasks ? 'Show Active Tasks' : 'Show Archived Tasks'}</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="bg-blue-50 p-3 rounded-lg">
                <svg className="w-6 h-6 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm text-primary-light">Total Tasks</p>
                <p className="text-xl font-semibold text-primary-headings">{totalTasks}</p>
              </div>
            </div>
          </div>

          {/* <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="bg-green-50 p-3 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm text-primary-light">Total Hours</p>
                <p className="text-xl font-semibold text-primary-headings">
                  {totalTime}
                </p>
              </div>
            </div>
          </div> */}

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="bg-purple-50 p-3 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm text-primary-light">{`Total ${chargeOutRateTitle}`}</p>
                <p className="text-xl font-semibold text-primary-headings">
                  ${totalCost.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Actions Bar */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4 items-center">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={inputValue}
              onChange={handleSearchChange}
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent text-sm"
              placeholder={showArchivedTasks ? "Search archived tasks..." : "Search tasks..."}
            />
          </div>

          {/* Category Filter Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              className="inline-flex items-center px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-200"
            >
              <IoFilter className="w-5 h-5 mr-2 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                Categories {selectedCategories.length > 0 && `(${selectedCategories.length})`}
              </span>
              {showCategoryDropdown ? (
                <IoChevronUp className="ml-2 w-4 h-4" />
              ) : (
                <IoChevronDown className="ml-2 w-4 h-4" />
              )}
            </button>

            {showCategoryDropdown && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <div className="p-3">
                  <div className="mb-2">
                    <input
                      type="text"
                      placeholder="Search categories..."
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light"
                      onChange={(e) => setCategorySearchTerm(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {filteredCategories
                      .map(category => (
                        <div
                          key={category._id}
                          className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer rounded-md"
                          onClick={() => handleCategorySelect(category._id)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedCategories.includes(category._id)}
                            onChange={() => { }}
                            className="h-4 w-4 text-primary-headings rounded border-gray-300"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            {category.categoryType}
                          </span>
                        </div>
                      ))
                    }
                    {filteredCategories.length === 0 && (
                      <div className="text-center text-gray-500 py-2">
                        No categories found
                      </div>
                    )}
                  </div>
                </div>
                <div className="border-t p-3 bg-gray-50 rounded-b-lg">
                  <button
                    onClick={() => setSelectedCategories([])}
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>

          {!showArchivedTasks && (
            <button
              onClick={handleCreateTask}
              className="inline-flex items-center px-6 py-2.5 text-white rounded-lg bg-cta hover:bg-opacity-90 transition-all duration-200 shadow-sm font-medium whitespace-nowrap"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create Task
            </button>
          )}
        </div>
      </div>

      <div className="pb-6">
        {/* Selected Filters Display - New Section */}
        {selectedCategories.length > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mt-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Selected Filters:</span>
              <div className="flex flex-wrap gap-2">
                {selectedCategories.slice(0, 5).map(categoryId => {
                  const category = categories.find(c => c._id === categoryId);
                  return category && (
                    <span
                      key={categoryId}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-accent-light text-accent-dark"
                    >
                      {category.categoryType}
                      <button
                        onClick={() => handleCategorySelect(categoryId)}
                        className="ml-2 hover:text-red-500"
                      >
                        <IoClose className="w-4 h-4" />
                      </button>
                    </span>
                  );
                })}

                {selectedCategories.length > 5 && (
                  <div className="relative">
                    <button
                      onClick={() => setShowMoreFilters(!showMoreFilters)}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      +{selectedCategories.length - 5} more
                      {showMoreFilters ? (
                        <IoChevronUp className="ml-1 w-4 h-4" />
                      ) : (
                        <IoChevronDown className="ml-1 w-4 h-4" />
                      )}
                    </button>

                    {showMoreFilters && (
                      <div className="absolute top-full w-auto min-w-[200px] max-h-[300px] overflow-y-auto left-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                        <div className="p-2">
                          {selectedCategories.slice(5).map(categoryId => {
                            const category = categories.find(c => c._id === categoryId);
                            return category && (
                              <div
                                key={categoryId}
                                className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 rounded-md gap-2"
                              >
                                <span className="text-sm text-gray-700">
                                  {category.categoryType}
                                </span>
                                <button
                                  onClick={() => handleCategorySelect(categoryId)}
                                  className="text-gray-400 hover:text-red-500"
                                >
                                  <IoClose className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {selectedCategories.length > 0 && (
                  <button
                    onClick={() => setSelectedCategories([])}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden max-h-[calc(100vh-480px)] flex flex-col mb-8">
        <div className="overflow-x-auto flex-1">
          <table className="w-full">
            <thead className="bg-white z-10 after:absolute after:inset-x-0 after:bottom-0 after:h-[1px] after:bg-gray-200">
              <tr>
                <th className="w-[60px] min-w-[60px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">No</th>
                <th className="w-full min-w-[200px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Title</th>
                <th className="w-[150px] min-w-[150px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Category</th>
                <th className="w-[100px] min-w-[100px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">{chargeOutRateTitle}</th>
                <th className="w-[120px] min-w-[120px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Min Time</th>
                <th className="w-[120px] min-w-[120px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Max Time</th>
                <th className="w-[120px] min-w-[120px] px-4 py-3 text-center text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading === 1 && (
                <tr>
                  <td colSpan="7" className="px-4 py-8">
                    <div className="flex items-center justify-center">
                      <div className="w-8 h-8 border-4 border-gray-300 rounded-full animate-spin" style={{ borderTopColor: colors.primary.headingsBody }}></div>
                    </div>
                  </td>
                </tr>
              )}

              {loading === 0 && taskList.map((task, index) => (
                <tr key={task._id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="w-[60px] min-w-[60px] px-4 py-2.5 text-sm font-medium text-primary-headings whitespace-nowrap">
                    {(pageVal - 1) * tasksPerPage + index + 1}
                  </td>
                  <td className="w-full min-w-[200px] px-4 py-2.5 max-w-[300px]">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-primary-headings truncate">
                        {task.title}
                        {showArchivedTasks && (
                          <span className="text-xs text-yellow-600 ml-2 bg-yellow-50 px-2 py-1 rounded-full italic">Archived</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="w-[150px] min-w-[150px] px-4 py-2.5">
                    <div className="overflow-x-auto">
                      <div className="inline-flex items-center gap-1">
                        <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-accent-light text-accent-dark whitespace-nowrap">
                          {task.category?.categoryType || "N/A"}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="w-[100px] min-w-[100px] px-4 py-2.5">
                    {(task.cost && task.cost > 0) ? (
                      <span className="text-sm font-semibold text-primary-headings whitespace-nowrap">
                        ${task.cost.toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400 whitespace-nowrap">-</span>
                    )}
                  </td>
                  <td className="w-[120px] min-w-[120px] px-4 py-2.5 text-sm text-primary-light whitespace-nowrap">
                    {task.estimatedMinTime ? `${Math.floor(task.estimatedMinTime / 60)}h ${task.estimatedMinTime % 60}m` : "N/A"}
                  </td>
                  <td className="w-[120px] min-w-[120px] px-4 py-2.5 text-sm text-primary-light whitespace-nowrap">
                    {task.estimatedMaxTime ? `${Math.floor(task.estimatedMaxTime / 60)}h ${task.estimatedMaxTime % 60}m` : "N/A"}
                  </td>
                  <td className="w-[120px] min-w-[120px] px-4 py-2.5">
                    <div className="flex justify-center space-x-2 overflow-x-auto">
                      <Link
                        to={`/view-task/${task._id}`}
                        state={{ taskData: task }}
                        className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors duration-200 shrink-0"
                        title="View"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IoEyeOutline size={16} className="text-blue-500" />
                      </Link>
                      {!showArchivedTasks && (
                        <button
                          onClick={() => handleEditClick(task._id)}
                          className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors duration-200 shrink-0"
                        >
                          <HiOutlinePencilAlt size={16} className="text-indigo-500" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArchiveTask(task._id, !showArchivedTasks);
                        }}
                        className="p-1.5 hover:bg-yellow-50 rounded-lg transition-colors duration-200 shrink-0"
                        title={showArchivedTasks ? "Unarchive" : "Archive"}
                        disabled={archivingTask === task._id}
                      >
                        {archivingTask === task._id ? (
                          <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <IoMdArchive size={16} className="text-yellow-600" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {loading === 0 && taskList.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <IoSearch size={48} className="text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
                      <p className="text-gray-500">Try adjusting your search or filters to find what you're looking for.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Section */}
        {!loading && taskList.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-100 bg-white">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-primary-light">
                Showing <span className="font-medium">{tasksPerPage * (pageVal - 1) + 1}</span> to{' '}
                <span className="font-medium">{tasksPerPage * (pageVal - 1) + taskList.length}</span> of{' '}
                <span className="font-medium">{totalTasks}</span> results
              </p>

              <nav className="flex items-center gap-2">
                <button
                  onClick={handlePreviousPageClick}
                  disabled={pageVal === 1}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50
                    ${pageVal === 1 ? 'bg-gray-100 text-primary-light' : 'bg-primary-headings text-white'}`}
                >
                  Previous
                </button>

                {totalPages > 0 && (
                  <>
                    <button
                      className={`px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-200
                        ${pageVal === pgItem1 ? 'bg-primary-headings text-white' : 'bg-gray-100 text-primary-light'}`}
                      onClick={() => setPageVal(pgItem1)}
                    >
                      {pgItem1}
                    </button>
                    {totalPages > 1 && (
                      <button
                        className={`px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-200
                          ${pageVal === pgItem2 ? 'bg-primary-headings text-white' : 'bg-gray-100 text-primary-light'}`}
                        onClick={() => setPageVal(pgItem2)}
                      >
                        {pgItem2}
                      </button>
                    )}
                    {totalPages > 2 && (
                      <button
                        className={`px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-200
                          ${pageVal === pgItem3 ? 'bg-primary-headings text-white' : 'bg-gray-100 text-primary-light'}`}
                        onClick={() => setPageVal(pgItem3)}
                      >
                        {pgItem3}
                      </button>
                    )}
                  </>
                )}

                <button
                  onClick={handleNextPageClick}
                  disabled={pageVal >= totalPages}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50
                    ${pageVal >= totalPages ? 'bg-gray-100 text-primary-light' : 'bg-primary-headings text-white'}`}
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <DeleteModal
          deleteItem="Task"
          onDelete={handleConfirmDelete}
          onClose={() => {
            setShowDeleteModal(false);
            setTaskToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default ListTask;
